// medplum-queue.js — ONLINE-PRIMARY, the idiomatic Medplum way (no view Bot).
// The browser reads today's Encounters DIRECTLY from Medplum (RLS-scoped by the
// doctor's own token, via the shared getMedplum() session — see medplum-client.js)
// and shapes them CLIENT-SIDE into the exact shapeQueueRow DTO the existing
// Queue.jsx already renders. This is how the Medplum Provider app works
// (useMedplum + searchResources + client-side util shaping) and it works OFFLINE
// (the same shaping runs over the local cache — no server round-trip).
import { getMedplum, deriveCurrentUser } from './medplum-client.js';
import { buildDateRangeFilter, loadEncounterWorklistBundle } from './medplum-bounded-search.js';
import { clinicTodayKey, clinicDayBounds, clinicDateKey, clinicDatePlusDays } from './clinic-time.js';
import { shapeQueueRow, groupEncounterBundle } from './shape/queue-row.js';
import { shapeHistoryRow } from './shape/history-row.js';

export async function loadQueueFromMedplum() {
  const { medplum, hospital } = await getMedplum();

  // FHIR-DIRECT read (no Bot): today's Encounters + their Patients + intake Compositions,
  // RLS-scoped to this doctor by their own token. _include/_revinclude fold in the related
  // resources on every page; Medplum applies the AccessPolicy to those too.
  //
  // Clinic-local "today" — SAME clinicTodayKey()/clinicDayBounds() helper Schedule.jsx
  // and Reception (medplum-reception.js) use. A raw `new Date().toISOString().slice(0,10)`
  // UTC date slice disagreed with those for the ~00:00-05:30 IST window each night (UTC
  // is still on yesterday's calendar date while the clinic is already into today), so
  // Queue's "Today" list could show yesterday's list past IST midnight even though
  // Schedule/Reception already thought it was "today".
  const today = clinicTodayKey();
  const { start, end } = clinicDayBounds(today);
  const baseQuery = `date=ge${start}&date=lt${end}&_include=Encounter:subject&_revinclude=Composition:encounter`;
  const { bundle, truncated, fetchedCount, maxRows } = await loadEncounterWorklistBundle(medplum, {
    baseQuery,
    dateFilterArg: buildDateRangeFilter(start, end, 'lt'),
  });

  const { patients, compsByEnc, encs } = groupEncounterBundle(bundle);
  const rows = encs.map((enc) => shapeQueueRow(enc, patients[enc.subject && enc.subject.reference] || null, compsByEnc['Encounter/' + enc.id] || []));

  // Same shape installBridge()/OPDBridge.pick('queue.rows') expects — Queue.jsx is
  // unchanged. currentUser is DERIVED from the live session's own Medplum
  // resources (deriveCurrentUser, cached per session), not static demo config.
  return {
    source: 'medplum-client-shaping',
    truncated,
    fetchedCount,
    maxRows,
    queue: { rows },
    currentUser: await deriveCurrentUser(medplum),
    hospital,
  };
}

// Prior-day history for the Yesterday / This-week tabs (queue + reception).
// Replaces the deleted Express GET /view/queue/history?days=N with ONE
// FHIR-direct Encounter date-range search over the whole window (RLS-scoped to
// the caller: a doctor sees their department's days, the front desk the whole
// clinic), bucketed client-side per day. Day boundaries are CLINIC-LOCAL
// (clinic-time.js), same convention as loadQueueFromMedplum's "today" and
// Reception's day tabs, so a row can never appear in both Today and Yesterday
// and history never disagrees with Today across the ~00:00-05:30 IST window.
// Returns { days: [{ date:'yyyy-mm-dd', label, rows }] } newest-first — the
// exact shape the tabs already render (label 'Yesterday' on the most recent
// prior day, matching the old server tagging Reception keys off).
export async function loadQueueHistoryFromMedplum({ days = 7 } = {}) {
  const { medplum } = await getMedplum();

  const today = clinicTodayKey();
  const startDay = clinicDatePlusDays(today, -days);
  const { start } = clinicDayBounds(startDay);
  const { start: end } = clinicDayBounds(today); // exclusive upper bound = clinic-local start of today
  const baseQuery = `date=ge${start}&date=lt${end}&_include=Encounter:subject&_revinclude=Composition:encounter`;
  const { bundle, truncated, fetchedCount, maxRows } = await loadEncounterWorklistBundle(medplum, {
    baseQuery,
    dateFilterArg: buildDateRangeFilter(start, end, 'lt'),
  });

  const { patients, compsByEnc, encs } = groupEncounterBundle(bundle);
  const byDay = new Map();
  for (const enc of encs) {
    const date = clinicDateKey((enc.period && enc.period.start) || '');
    if (!date) continue; // no period.start → undateable, leave it off the day tabs
    if (!byDay.has(date)) byDay.set(date, []);
    // History rows carry the settled-visit extras (doctor · department · timing)
    // on top of the live-queue fields — see shape/history-row.js. Additive to the
    // shapeQueueRow DTO, so Reception's day tabs (same loader) keep working.
    byDay.get(date).push(shapeHistoryRow(enc, patients[enc.subject && enc.subject.reference] || null, compsByEnc['Encounter/' + enc.id] || []));
  }

  const yesterday = clinicDatePlusDays(today, -1);
  const ordered = [...byDay.keys()].sort().reverse(); // newest prior day first
  return {
    truncated,
    fetchedCount,
    maxRows,
    days: ordered.map((date) => ({
      date,
      label: date === yesterday ? 'Yesterday' : date,
      rows: byDay.get(date),
    })),
  };
}
